import styled, { css } from "styled-components";
import { TFunction, useTranslation } from "react-i18next";
import { Activation } from "api";
import { LoadState } from "features/activation";
import { downloadFile } from "common/download";
import {
  Column,
  Item,
  Loading,
  Message,
  Table,
  TableHeader,
  TableWrap,
  ToolTip,
  getDate,
} from "components/Table";
import ActivationPagination from "./ActivationPagination";

export interface ActivationTableProps {
  loadState: LoadState;
  dataList: Activation[];
  maxPage: number;
  pageIndex: number;
  onPageChange: (selectedItem: { selected: number }) => void;
  onRevoke: (aId: string) => void;
  onGeneratePermissionTicket: (aId: string) => Promise<void>;
}

const statusTable = new Map([
  ["ACTIVATED", "activationPage.status.activated"],
  ["REVOCATION_IN_PROGRESS", "activationPage.status.revocationInProgress"],
  ["REVOKE_CONFIRMED", "activationPage.status.revokeConfirmed"],
]);

const ActivationTable: React.FC<ActivationTableProps> = (
  props: ActivationTableProps
): JSX.Element => {
  const { t } = useTranslation();
  const {
    loadState,
    dataList,
    maxPage,
    pageIndex,
    onPageChange,
    onGeneratePermissionTicket,
    onRevoke,
  } = props;

  return (
    <TableWrap>
      <ActivationPagination
        maxPage={maxPage}
        pageIndex={pageIndex}
        onPageChange={onPageChange}
        loading={loadState === "Loading"}
      />
      <Table>
        <thead>
          <TableHeader>
            <Column>
              {t("activationPage.table.activationId")}
              <ToolTip className="tooltip">
                {t("activationPage.help.activationId")}
              </ToolTip>
            </Column>
            <Column>
              {t("activationPage.table.licenseNumber")}
              <ToolTip className="tooltip">
                {t("activationPage.help.licenseNumber")}
              </ToolTip>
            </Column>
            <Column>
              {t("activationPage.table.productName")}
              <ToolTip className="tooltip">
                {t("activationPage.help.productName")}
              </ToolTip>
            </Column>
            <Column>
              {t("activationPage.table.licenseModel")}
              <ToolTip className="tooltip">
                {t("activationPage.help.licenseModel")}
              </ToolTip>
            </Column>
            <Column>
              {t("activationPage.table.activationDate")}
              <ToolTip className="tooltip">
                {t("activationPage.help.activationDate")}
              </ToolTip>
            </Column>
            <Column>
              {t("activationPage.table.status")}
              <ToolTip className="tooltip">
                {t("activationPage.help.status")}
              </ToolTip>
            </Column>
            <ButtonColumn>
              {t("activationPage.table.licenseCode")}
              <ToolTip className="tooltip">
                {t("activationPage.help.licenseCode")}
              </ToolTip>
            </ButtonColumn>
            <ButtonColumn>
              {t("activationPage.table.permissionTicket")}
              <ToolTip className="tooltip">
                {t("activationPage.help.permissionTicket")}
              </ToolTip>
            </ButtonColumn>
            <ButtonColumn>
              {t("activationPage.table.revoke")}
              <ToolTip className="tooltip">
                {t("activationPage.help.revoke")}
              </ToolTip>
            </ButtonColumn>
          </TableHeader>
        </thead>
        <tbody>
          {loadState === "Loading" && (
            <tr>
              <td colSpan={11}>
                <Loading />
              </td>
            </tr>
          )}
          {loadState === "LoadError" && (
            <tr>
              <td colSpan={11}>
                <Message style={{ color: "red" }}>
                  {t("activationPage.message.faildGetData")}
                </Message>
              </td>
            </tr>
          )}
          {loadState === "Loaded" && dataList.length === 0 && (
            <tr>
              <td colSpan={11}>
                <Message>{t("activationPage.message.dataEmpty")}</Message>
              </td>
            </tr>
          )}
          {loadState === "Loaded" &&
            dataList.length !== 0 &&
            dataList.map((data) => (
              <tr key={`${data.id}`}>
                <Item key={`${data.id}_aId`}>{data.aId}</Item>
                <Item key={`${data.id}_eId`}>{data.eId}</Item>
                <Item key={`${data.id}_productName`}>{data.productName}</Item>
                <Item key={`${data.id}_licenseModelName`}>
                  {data.licenseModelName}
                </Item>
                <Item key={`${data.id}_activationDate`}>
                  {getDate(data.activationDate)}
                </Item>
                <Item key={`${data.id}_status`}>
                  {t(statusTable.get(data.status) ?? "")}
                </Item>
                <Item key={`${data.id}_licenseCode`}>
                  <Button
                    disabled={data.status !== "ACTIVATED"}
                    onClick={() => {
                      downloadFile(
                        "lservrc",
                        data.licenseKey,
                        "application/octet-stream"
                      );
                    }}
                  >
                    {t("activationPage.button.download")}
                  </Button>
                </Item>
                <Item key={`${data.id}_permissionTicket`}>
                  <Button
                    disabled={
                      (data.status !== "ACTIVATED" &&
                        data.status !== "REVOCATION_IN_PROGRESS") ||
                      data.isTrial
                    }
                    onClick={() => {
                      onGeneratePermissionTicket(data.aId);
                    }}
                  >
                    {t("activationPage.button.generate")}
                  </Button>
                </Item>
                <Item key={`${data.id}_revoke`}>
                  <Button
                    disabled={data.status !== "REVOCATION_IN_PROGRESS"}
                    onClick={() => {
                      onRevoke(data.aId);
                    }}
                  >
                    {t("activationPage.button.revoke")}
                  </Button>
                </Item>
              </tr>
            ))}
        </tbody>
      </Table>
      <ActivationPagination
        maxPage={maxPage}
        pageIndex={pageIndex}
        onPageChange={onPageChange}
        loading={loadState === "Loading"}
      />
    </TableWrap>
  );
};

export default ActivationTable;

const ButtonColumn = styled(Column)`
  width: 10%;
`;

const Button = styled.a<{ color?: string; disabled?: boolean }>`
  display: block;
  padding: 12px 0;
  color: #ffffff;
  text-decoration: none;
  background: #005eb8;
  border: 1px #005eb8 solid;
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  cursor: pointer;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
  ${({ disabled }) =>
    disabled &&
    css`
      color: #999999;
      background: #f5f5f5;
      border: 1px #e6e6e6 solid;
      pointer-events: none;
      :hover {
        background: #fafafa;
      }
    `}

  ${({ color }) =>
    color === "red" &&
    css`
      color: #ffffff;
      background: #e50000;
      border: 1px #e50000 solid;
      :hover {
        background: rgba(229, 0, 0, 0.7);
      }
    `}
`;
